import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { creativeTemplates, type CreativeTemplate } from '../../lib/creativeTemplates';

interface TemplateLibraryProps {
  onInsert: (text: string) => void;
  className?: string;
}

/**
 * Lightweight "Templates" popover — a curated library of creative best-practices +
 * parser-safe request patterns. No Radix/cmdk: just a positioned dropdown div,
 * framer-motion fade/scale, click-outside + Esc to close.
 */
export function TemplateLibrary({ onInsert, className }: TemplateLibraryProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // click-outside + Esc to close
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // focus the search when opened
  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
    else setQuery('');
  }, [open]);

  // group filtered templates by category, preserving source order
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const items = q
      ? creativeTemplates.filter(t =>
          (t.title + ' ' + t.description + ' ' + t.insert + ' ' + t.category)
            .toLowerCase()
            .includes(q)
        )
      : creativeTemplates;
    const out: { category: string; items: CreativeTemplate[] }[] = [];
    for (const t of items) {
      let bucket = out.find(b => b.category === t.category);
      if (!bucket) {
        bucket = { category: t.category, items: [] };
        out.push(bucket);
      }
      bucket.items.push(t);
    }
    return out;
  }, [query]);

  const pick = (t: CreativeTemplate) => {
    onInsert?.(t.insert);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium',
          'text-violet-600 dark:text-violet-300',
          'border border-violet-200 dark:border-violet-800/60',
          'bg-violet-50/60 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/40',
          'transition-colors'
        )}
      >
        <Sparkles className="w-3.5 h-3.5" />
        Templates
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.12, ease: 'easeOut' }}
            className={cn(
              'absolute right-0 z-50 mt-2 w-80 origin-top-right',
              'rounded-xl border border-gray-200 dark:border-gray-700',
              'bg-white dark:bg-gray-900 shadow-xl',
              'flex flex-col overflow-hidden'
            )}
          >
            {/* search */}
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 px-3 py-2">
              <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search templates…"
                className="w-full bg-transparent text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none"
              />
            </div>

            {/* list */}
            <div className="max-h-80 overflow-y-auto py-1">
              {grouped.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-gray-400">No templates match "{query}".</div>
              )}
              {grouped.map(group => (
                <div key={group.category} className="py-1">
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-violet-500/80 dark:text-violet-400/70">
                    {group.category}
                  </div>
                  {group.items.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => pick(t)}
                      className={cn(
                        'w-full text-left px-3 py-1.5',
                        'hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors'
                      )}
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{t.description}</div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default TemplateLibrary;
